import React, { useEffect, useState } from 'react';
import { prepareHeaders } from 'utils'; // Adjust the import path as necessary
import { useGetMyProfileQuery } from 'app/services/user';
import { useGetAccountantProfilesQuery } from 'app/services/accountant';
import './BillingComponent.css'; // Import the CSS file
import { useNavigate } from 'react-router-dom';

interface BillingBundle {
  id: number;
  name: string;
  user_count: number;
  price: number;
  interval: string;
}

const BillingComponent = () => {
  const { data: profileData } = useGetMyProfileQuery();
  const {
    data: accountantProfileData,
    isLoading,
    error,
  } = useGetAccountantProfilesQuery(profileData?.user?.id || 0);

  const [billingBundles, setBillingBundles] = useState<BillingBundle[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<number | null>(null); // Track selected bundle
  const [currentBundle, setCurrentBundle] = useState<BillingBundle | null>(
    null,
  ); // Track current assigned bundle
  const [showConfirmation, setShowConfirmation] = useState(false); // State for confirmation popup

  const navigate = useNavigate();

  useEffect(() => {
    if (accountantProfileData) {
      fetchBillingBundles(accountantProfileData.accountant_profile.id);
    }
  }, [accountantProfileData]);

  const fetchBillingBundles = async (profileId: number) => {
    try {
      const headers = await prepareHeaders(new Headers());
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/billing_bundles`,
        { headers },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const bundles = await response.json();

      const currentBundle = bundles.find(
        (bundle: BillingBundle) =>
          bundle.id ===
          accountantProfileData?.accountant_profile.billing_bundle_id,
      );
      setCurrentBundle(currentBundle);
      setBillingBundles(bundles);
    } catch (error) {
      console.error('Error fetching billing bundles:', error);
    }
  };

  const handleBundleClick = async (bundleId: number, userCount: number) => {
    if (
      (accountantProfileData?.accountant_profile.sub_accountants_count ?? 0) <
        userCount &&
      currentBundle?.id !== bundleId
    ) {
      if (currentBundle) {
        setShowConfirmation(true);
        setSelectedBundle(bundleId);
      } else {
        await selectBundle(bundleId);
      }
    }
  };

  const handleConfirmChangeBundle = async () => {
    setShowConfirmation(false);
    await selectBundle(selectedBundle);
  };

  const selectBundle = async (bundleId: number | null) => {
    if (bundleId === null) return;
    try {
      const headers = await prepareHeaders(new Headers());
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/billing_bundles/${bundleId}/select`,
        {
          method: 'POST',
          headers,
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url; // Redirect to the checkout URL
      }
    } catch (error) {
      console.error('Error selecting billing bundle:', error);
    }
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p>Error loading data</p>;
  }

  return (
    <div className="billing-container">
      <div className="billing-content">
        <h1>Billing Bundles</h1>
        <hr />
        <div className="current-status">
          <h2>
            Your Current Assigned Businesses:{' '}
            {accountantProfileData?.accountant_profile.business_count ?? 0}
          </h2>
          <h2>
            Your Current Sub-Accountants (Users):{' '}
            {accountantProfileData?.accountant_profile.sub_accountants_count ??
              0}
          </h2>
          {currentBundle && (
            <h3>
              Current Bundle: {currentBundle.name} (User Count:{' '}
              {currentBundle.user_count}, Price: {currentBundle.price}$ per user
              per {currentBundle.interval})
            </h3>
          )}
        </div>
        <div className="bundle-container">
          <ul className="bundle-list">
            {billingBundles
              .filter((bundle) => bundle.interval === 'month')
              .map((bundle) => {
                const isCurrent = currentBundle?.id === bundle.id;
                const isSelected = selectedBundle === bundle.id;
                return (
                  <li
                    key={bundle.id}
                    className={`bundle-item ${
                      isCurrent ? 'current-plan' : ''
                    } ${isSelected ? 'selected' : 'selectable'}`}
                    onClick={() =>
                      !isCurrent &&
                      handleBundleClick(bundle.id, bundle.user_count)
                    }
                    title={isCurrent ? 'This is your current plan' : ''}
                  >
                    <h2>{bundle.name}</h2>
                    <p>Number of Users: {bundle.user_count}</p>
                    <p>Total Price: {bundle.price}$</p>
                  </li>
                );
              })}
          </ul>
          <ul className="bundle-list">
            {billingBundles
              .filter((bundle) => bundle.interval === 'year')
              .map((bundle) => {
                const isCurrent = currentBundle?.id === bundle.id;
                const isSelected = selectedBundle === bundle.id;
                return (
                  <li
                    key={bundle.id}
                    className={`bundle-item ${
                      isCurrent ? 'current-plan' : ''
                    } ${isSelected ? 'selected' : 'selectable'}`}
                    onClick={() =>
                      !isCurrent &&
                      handleBundleClick(bundle.id, bundle.user_count)
                    }
                    title={isCurrent ? 'This is your current plan' : ''}
                  >
                    <h2>{bundle.name}</h2>
                    <p>Number of Users: {bundle.user_count}</p>
                    <p>Total Price: {bundle.price}$</p>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="enterprise-bundle-container">
          <div
            className="enterprise-bundle"
            onClick={() => window.open('https://wkf.ms/3W2BNLJ', '_blank')}
          >
            <h2>Enterprise Plan</h2>
            <p>Contact us for more details</p>
          </div>
        </div>
        {showConfirmation && (
          <div className="confirmation-popup">
            <div className="confirmation-content">
              <h2>Are you sure you want to change your bundle?</h2>
              <button onClick={handleConfirmChangeBundle}>Yes</button>
              <button onClick={() => setShowConfirmation(false)}>No</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingComponent;
